/* ActivityStateBridge — editor-side glue wiring the live ScratchJr editor into
 * the runtime-agnostic ActivityState observation surface (web/ai/activity-state).
 * Every helper is a safe no-op when ActivityState has no configured source. @see #1116 */
import ActivityState from '../../../../../web/ai/activity-state/ActivityState';
import ScratchJr from '../../ScratchJr';
import Project from '../../ui/Project';

const DT = ActivityState.DELTA_TYPES;
const RUN_STARTED = ['RUNNING', 'EVALUATING'];
const RUN_COMPLETED = ['SUCCESS', 'NEEDS_WORK', 'FAILED'];

let stateMachine = null;
let stateListener = null;
let monitor = null;
let monitorOrig = null;
let monitorWrapped = false;

function paletteAvailable (c) {
    try {
        const r = c && c.plugin && c.plugin._paletteRestrictor;
        if (r && r._allowedBlocks) return Array.from(r._allowedBlocks);
    } catch (e) { /* palette restriction is optional */ }
    return [];
}

function buildSource (c) {
    return {
        getProject: () => Project.getProject(),
        getActivityId: () => (c && c.activityData && (c.activityData.id || c.activityData.activityId)) || null,
        getCurrentPageIndex: () => (ScratchJr.stage && ScratchJr.stage.pages ? ScratchJr.stage.pages.indexOf(ScratchJr.stage.currentPage) : 0),
        getCurrentSpriteId: () => (ScratchJr.stage && ScratchJr.stage.currentPage ? ScratchJr.stage.currentPage.currentSpriteName : null),
        getPaletteAvailable: () => paletteAvailable(c)
    };
}

function handleStateChange (prev, next) {
    if (RUN_STARTED.indexOf(next) > -1) ActivityState.notify({type: DT.RUN_STARTED});
    else if (RUN_COMPLETED.indexOf(next) > -1) ActivityState.notify({type: DT.RUN_COMPLETED});
}

function install (opts) {
    uninstall(); // idempotent: drop any prior wiring (and ActivityState source) first
    const c = opts && opts.controller;
    ActivityState.configure({source: buildSource(c)});
    if (c && c.stateMachine && typeof c.stateMachine.onStateChange === 'function') {
        stateMachine = c.stateMachine;
        stateListener = handleStateChange;
        stateMachine.onStateChange(stateListener);
    }
}

// Wrap an InactivityMonitor's _onInactive so an idle crossing also emits a delta;
// no-op if absent / already attached. ActivityUI owns this monitor's lifecycle.
function attachInactivityMonitor (im) {
    if (!im || monitorWrapped) return;
    monitor = im;
    monitorOrig = im._onInactive;
    monitorWrapped = true;
    im._onInactive = function () {
        if (typeof monitorOrig === 'function') monitorOrig.call(im);
        ActivityState.notify({type: DT.IDLE_THRESHOLD_CROSSED});
    };
}

function detachInactivityMonitor () {
    if (monitor && monitorWrapped) monitor._onInactive = monitorOrig;
    monitor = null;
    monitorOrig = null;
    monitorWrapped = false;
}

function uninstall () {
    if (stateMachine && stateListener && typeof stateMachine.offStateChange === 'function') stateMachine.offStateChange(stateListener);
    detachInactivityMonitor();
    stateMachine = null;
    stateListener = null;
    ActivityState.reset();
}

function recordBlockPlaced (blockType, spriteId) { ActivityState.notify({type: DT.BLOCK_PLACED, blockType, spriteId}); }
function recordBlockRemoved (blockType, spriteId) { ActivityState.notify({type: DT.BLOCK_REMOVED, blockType, spriteId}); }
function recordSpriteSelected (spriteId) { ActivityState.notify({type: DT.SPRITE_SELECTED, spriteId}); }
// pageIndex reaches onChange subscribers but is intentionally dropped from observe().lastAction (only {type, blockType, spriteId, at} persist — see ActivityState.notify and spec §2).
function recordPageChanged (pageIndex) { ActivityState.notify({type: DT.PAGE_CHANGED, pageIndex}); }

export default {
    install,
    uninstall,
    attachInactivityMonitor,
    detachInactivityMonitor,
    recordBlockPlaced,
    recordBlockRemoved,
    recordSpriteSelected,
    recordPageChanged
};
